import type { ReplayFile } from '../../replay/replay';
import type { ArenaGameplayDefinition, BattleEvent, BattleState, CardId, EntityId, EntityState, PlayerId, ProjectileState, WorldPoint } from '../types';
import type { MechanicEmitter, SimulationRecorder } from './system-context';

export const otherSide = (side: PlayerId): PlayerId => side === 'player' ? 'bot' : 'player';
export const kingSocket = (side: PlayerId) => side === 'player' ? 'playerKing' as const : 'botKing' as const;

export interface LifecycleSystemContext {
  readonly state: BattleState;
  readonly arena: ArenaGameplayDefinition;
  readonly replay: ReplayFile;
  readonly events: BattleEvent[];
  readonly cancelPendingAttack: (entity: EntityState, reason: 'source-died' | 'match-ended') => void;
  readonly cancelLeap: (entity: EntityState) => void;
  readonly emitMechanic: MechanicEmitter;
  readonly record: SimulationRecorder;
}

export class BattleLifecycleSystem {
  constructor(private readonly context: LifecycleSystemContext) {}

  resolveDeathsAndResult(): void {
    const { state, events } = this.context;
    const kingWinners = new Set<PlayerId>();
    for (const entity of state.entities.sort((left, right) => left.id - right.id)) {
      if (entity.dead || entity.removed || entity.hp > 0) continue;
      this.context.cancelPendingAttack(entity, 'source-died');
      this.context.cancelLeap(entity);
      entity.dead = true;
      entity.targetId = null;
      events.push({ type: 'entity-died', tick: state.tick, entityId: entity.id, owner: entity.owner });
      this.context.record('ENTITY_DIED', { owner: entity.owner, kind: entity.kind }, { entityIds: [entity.id], cardIds: [entity.sourceCardId] });
      if (entity.deathEffect) this.spawnDeathEffect(entity);
      if (entity.kind === 'tower') {
        const winner = otherSide(entity.owner);
        const kingDestroyed = entity.arenaSocketId === kingSocket(entity.owner);
        state.players[winner].crowns = kingDestroyed ? 3 : Math.min(2, state.players[winner].crowns + 1);
        events.push({ type: 'tower-destroyed', tick: state.tick, entityId: entity.id, crowns: state.players[winner].crowns });
        this.context.record('TOWER_DESTROYED', { winner, crowns: state.players[winner].crowns }, { entityIds: [entity.id] });
        if (entity.arenaSocketId) {
          const unlocked = this.context.arena.deploymentZones.filter((zone) => zone.owner === winner && zone.unlockTowerSocketId === entity.arenaSocketId);
          for (const zone of unlocked) {
            events.push({ type: 'deployment-zone-unlocked', tick: state.tick, playerId: winner, zoneId: zone.id, laneId: zone.laneId, towerSocketId: entity.arenaSocketId });
            this.context.record('DEPLOYMENT_ZONE_UNLOCKED', { playerId: winner, zoneId: zone.id, laneId: zone.laneId, towerSocketId: entity.arenaSocketId }, { entityIds: [entity.id] });
          }
        }
        const king = state.entities.find((candidate) => candidate.arenaSocketId === kingSocket(entity.owner));
        if (!kingDestroyed && king && !king.active) this.activateTower(king, 'princess-destroyed');
        if (kingDestroyed) kingWinners.add(winner);
      }
    }
    if (kingWinners.size === 1) this.finish([...kingWinners][0]!, 'king-destroyed');
    else if (kingWinners.size > 1) this.finish('draw', 'king-destroyed');
    else if (state.phase === 'overtime' && state.players.player.crowns !== state.players.bot.crowns) this.finish(state.players.player.crowns > state.players.bot.crowns ? 'player' : 'bot', 'sudden-death');
  }

  activateTower(tower: EntityState, reason: 'direct-damage' | 'princess-destroyed'): void {
    if (tower.active || tower.dead || tower.removed) return;
    tower.active = true;
    this.context.events.push({ type: 'tower-activated', tick: this.context.state.tick, entityId: tower.id });
    this.context.record('TOWER_ACTIVATED', { reason }, { entityIds: [tower.id], cardIds: [tower.sourceCardId] });
  }

  finishAfterOvertime(): void {
    const { player, bot } = this.context.state.players;
    if (player.crowns !== bot.crowns) this.finish(player.crowns > bot.crowns ? 'player' : 'bot', 'time');
    else this.finishTiebreak();
  }

  finishTiebreak(): void {
    const lowestTowerHp = (owner: PlayerId): number => Math.min(...this.context.state.entities.filter((entity) => entity.owner === owner && entity.kind === 'tower' && !entity.dead).map((entity) => entity.hp));
    const playerHealth = lowestTowerHp('player');
    const botHealth = lowestTowerHp('bot');
    this.finish(playerHealth === botHealth ? 'draw' : playerHealth > botHealth ? 'player' : 'bot', 'tiebreak');
  }

  finish(winner: PlayerId | 'draw', reason: 'king-destroyed' | 'time' | 'sudden-death' | 'tiebreak'): void {
    const { state, events, replay } = this.context;
    if (state.result) return;
    for (const entity of state.entities) {
      this.context.cancelPendingAttack(entity, 'match-ended');
      this.context.cancelLeap(entity);
    }
    for (const projectile of state.projectiles) {
      if (projectile.dead) continue;
      projectile.dead = true;
      if (projectile.mechanic === 'death-damage') this.context.emitMechanic(projectile.sourceId, projectile.sourceCardId, 'death-damage', 'cancel', projectile.visualKey, projectile.position);
      events.push({ type: 'projectile-cancelled', tick: state.tick, projectileId: projectile.id, sourceId: projectile.sourceId, visualKey: projectile.visualKey, reason: 'match-ended' });
      this.context.record('PROJECTILE_CANCELLED', { reason: 'match-ended', position: projectile.position, targetPoint: projectile.targetPoint }, { entityIds: [projectile.id, ...(projectile.sourceId === null ? [] : [projectile.sourceId])], cardIds: [projectile.sourceCardId] });
    }
    state.phase = 'ended';
    state.result = { winner, reason, crowns: { player: state.players.player.crowns, bot: state.players.bot.crowns }, tick: state.tick };
    replay.result = structuredClone(state.result);
    events.push({ type: 'battle-ended', tick: state.tick, result: structuredClone(state.result) });
    this.context.record('BATTLE_ENDED', state.result);
  }

  private spawnDeathEffect(entity: EntityState): void {
    const effect = entity.deathEffect!;
    const state = this.context.state;
    const projectile: ProjectileState = {
      id: state.nextEntityId++, owner: entity.owner, sourceId: entity.id, sourceCardId: entity.sourceCardId, targetId: null,
      targetPoint: { ...entity.position }, position: { ...entity.position }, speedPerTick: 0, damage: effect.damage, splashRadius: effect.radius,
      targetMask: 'all-enemies', towerDamageMultiplier: effect.towerDamageMultiplier, remainingTicks: Math.max(1, effect.delayTicks),
      visualKey: effect.visualKey, dead: false, behavior: 'damage', mechanic: 'death-damage',
    };
    state.projectiles.push(projectile);
    this.context.emitMechanic(entity.id, entity.sourceCardId, 'death-damage', 'start', effect.visualKey, entity.position);
    this.context.events.push({ type: 'projectile-spawned', tick: state.tick, projectileId: projectile.id, sourceId: entity.id, visualKey: projectile.visualKey });
    this.context.record('PROJECTILE_SPAWNED', { projectileId: projectile.id, mechanic: 'death-damage', delayTicks: effect.delayTicks }, { entityIds: [entity.id, projectile.id], cardIds: [entity.sourceCardId] });
  }
}
